/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module SharedModule
 */

import {
    Component,
    EventEmitter,
    Input,
    Output,
} from '@angular/core';

import './avi-alert-group.component.less';
import * as globalL10n from 'global-l10n';

export interface IAlertObject {
    message: string;
    status: string;
    closable?: boolean;
    onClose?: () => {};
}

const { ENGLISH: dictionary, ...globalL10nKeys } = globalL10n;

/**
 * @description
 *    Component can be used for displaying single/multiple alert messages
 *    Alert Type can be set as danger, warning, success, info
 *    Alert message can be set as string
 *
 *    If closable, the alert will show an "x" button.
 *    This component exposes onAlertClose Output for when that button is clicked.
 *    Parent component must close this component.
 *
 *    To display multiple warning or error messages, pass multipleAlerts as true &
 *    pass value to warningResponseSampleResponse.
 *
 * @example
 *     <avi-alert-group alertType = "danger">
 *          <p>
 *               Some msg here
 *          </p>
 *           <p>
 *              Some msg here
 *           </p>
 *      </avi-alert-group>
 *
 * @author Shahab Hashmi, Alex Klyuev
 */

@Component({
    selector: 'avi-alert-group',
    templateUrl: './avi-alert-group.component.html',
})
export class AviAlertGroupComponent {
    /**
     * Input for deprecation message.
     */
    @Input()
    public deprecationLabel?: string;

    /**
     * Binding for hyperlink.
     */
    @Input()
    public hyperlink?: string;

    /**
     * Input for alert type.
     */
    @Input()
    public alertType: string;

    /**
     * Boolean Input for close button.
     */
    @Input()
    public closable = false;

    /**
     * Alerts to be displayed in the UI.
     */
    @Input()
    public alerts?: IAlertObject[];

    @Input()
    public size = 'sm';

    /**
     * Fire on alert closing.
     */
    @Output()
    public onAlertClose = new EventEmitter();

    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * Handle close action of cds-alerts.
     */
    public handleClose(alert?: IAlertObject): void {
        if (alert?.onClose) {
            alert.onClose();
        }

        this.onAlertClose.emit();
    }

    /**
     * Open hyperlink in new tab.
     */
    public redirectToExternalLink(): void {
        window.open(this.hyperlink, '_blank');
    }
}
